import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  ArrowLeft,
  ArrowRight,
  Save,
  Send,
  Sparkles,
  Loader2,
  Monitor,
  Smartphone,
  Eye,
  ChevronDown,
  ChevronRight,
  Paintbrush,
  Type,
  Image as ImageIcon,
  Palette,
  Plus,
  Trash2,
  GripVertical,
  X,
  Calendar,
  Clock,
  Users,
  Mail,
  FileText,
  CheckCircle,
  Wand2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { templatesApi, aiApi, n8nApi, mauticApi, sectorsApi, messageTypesApi } from '../lib/api';
import { useCredits } from '../context/CreditsContext';
import { useAuth } from '../context/AuthContext';

// Array Editor Dialog Component
const ArrayEditorDialog = ({ open, onOpenChange, field, value, onChange }) => {
  const [items, setItems] = useState([]);
  
  useEffect(() => {
    if (open) {
      const initialValue = Array.isArray(value) ? value : (field.default || []);
      setItems(initialValue.map((item, idx) => ({ ...item, _id: idx })));
    }
  }, [open, value, field.default]);

  const handleAddItem = () => {
    // Get structure from first item or field default
    const template = items[0] || (field.default && field.default[0]) || { name: '', price: '', description: '', imageUrl: '', link: '' };
    const newItem = Object.keys(template).reduce((acc, key) => {
      if (key !== '_id') acc[key] = '';
      return acc;
    }, {});
    newItem._id = Date.now();
    setItems([...items, newItem]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, key, newValue) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [key]: newValue };
    setItems(updated);
  };

  const handleSave = () => {
    // Remove _id before saving
    const cleanItems = items.map(({ _id, ...rest }) => rest);
    onChange(cleanItems);
    onOpenChange(false);
    toast.success('Elementos guardados');
  };

  // Determine item fields from structure
  const getItemFields = () => {
    const sample = items[0] || (field.default && field.default[0]) || {};
    return Object.keys(sample).filter(k => k !== '_id');
  };

  const itemFields = getItemFields();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Editar {field.label}</DialogTitle>
          <DialogDescription>
            Añade, edita o elimina elementos de la lista
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4">
            {items.map((item, index) => (
              <Card key={item._id} className="relative">
                <CardContent className="pt-4">
                  <div className="absolute top-2 right-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleRemoveItem(index)}
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pr-8">
                    {itemFields.map(fieldKey => (
                      <div key={fieldKey} className={fieldKey === 'description' || fieldKey === 'link' ? 'col-span-2' : ''}>
                        <Label className="text-xs text-slate-500 capitalize">{fieldKey}</Label>
                        <Input
                          value={item[fieldKey] || ''}
                          onChange={(e) => handleItemChange(index, fieldKey, e.target.value)}
                          placeholder={fieldKey}
                          className="mt-1"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {items.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No hay elementos. Añade uno para empezar.
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={handleAddItem}>
            <Plus className="w-4 h-4 mr-2" />
            Añadir elemento
          </Button>
          <Button onClick={handleSave}>
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// AI Improve Button Component
const AIImproveButton = ({ field, value, onImprove, disabled, messageType, sector }) => {
  const [loading, setLoading] = useState(false);
  const { credits, refreshCredits } = useCredits();

  const handleImprove = async () => {
    if (credits.balance < 1) {
      toast.error('Créditos insuficientes');
      return;
    }

    setLoading(true);
    try {
      const response = await aiApi.improveField({
        text: value,
        context: '',
        messageType: messageType,
        fieldType: field.key,
        sector: sector,
      });
      onImprove(response.data.improved);
      refreshCredits();
      toast.success('Texto mejorado');
    } catch (error) {
      toast.error('Error al mejorar el texto');
    } finally {
      setLoading(false);
    }
  };

  if (!field.aiImprove) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleImprove}
      disabled={loading || disabled || !value}
      className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
      data-testid={`ai-improve-${field.key}`}
    >
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Sparkles className="w-3 h-3" />
      )}
      <span className="ml-1 text-xs">Mejorar</span>
    </Button>
  );
};

// Section Editor Component
const SectionEditor = ({ section, data, onChange, messageType, sector, expanded, onToggle }) => {
  const [arrayEditorOpen, setArrayEditorOpen] = useState(false);
  const [editingArrayField, setEditingArrayField] = useState(null);
  
  const handleFieldChange = (key, value) => {
    onChange({ ...data, [key]: value });
  };

  const openArrayEditor = (field) => {
    setEditingArrayField(field);
    setArrayEditorOpen(true);
  };

  const renderField = (field) => {
    const value = data[field.key] ?? field.default;

    if (field.type === 'textarea') {
      return (
        <div key={field.key} className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor={field.key}>{field.label}</Label>
            <AIImproveButton 
              field={field} 
              value={value}
              onImprove={(v) => handleFieldChange(field.key, v)}
              messageType={messageType}
              sector={sector}
            />
          </div>
          <Textarea
            id={field.key}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.default}
            className="min-h-[100px]"
            data-testid={`field-${section.id}-${field.key}`}
          />
        </div>
      );
    }

    if (field.type === 'image') {
      return (
        <div key={field.key} className="space-y-2">
          <Label htmlFor={field.key}>{field.label}</Label>
          <Input
            id={field.key}
            type="url"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder="https://..."
            data-testid={`field-${section.id}-${field.key}`}
          />
          {value && (
            <img 
              src={value} 
              alt="Preview" 
              className="mt-2 max-h-32 rounded-lg object-cover"
            />
          )}
        </div>
      );
    }

    if (field.type === 'array') {
      // Handle array fields like products, items, etc.
      const arrayValue = Array.isArray(value) ? value : (field.default || []);
      return (
        <div key={field.key} className="space-y-2">
          <Label>{field.label}</Label>
          <p className="text-xs text-slate-500">
            {arrayValue.length} elementos configurados
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => openArrayEditor(field)}
            data-testid={`edit-array-${field.key}`}
          >
            Editar elementos
          </Button>
        </div>
      );
    }

    return (
      <div key={field.key} className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={field.key}>{field.label}</Label>
          <AIImproveButton 
            field={field} 
            value={value}
            onImprove={(v) => handleFieldChange(field.key, v)}
            messageType={messageType}
            sector={sector}
          />
        </div>
        <Input
          id={field.key}
          value={value}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          placeholder={field.default}
          data-testid={`field-${section.id}-${field.key}`}
        />
      </div>
    );
  };

  return (
    <>
      <div 
        className={`border rounded-lg overflow-hidden transition-all ${expanded ? 'border-indigo-200 shadow-sm' : 'border-slate-200'}`}
        data-testid={`section-${section.id}`}
      >
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
            <div className="text-left">
              <p className="font-medium text-slate-900">{section.label}</p>
              <p className="text-xs text-slate-500">{section.type}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {section.fields?.length || 0} campos
          </Badge>
        </button>
        
        {expanded && (
          <div className="p-4 pt-0 space-y-4 border-t bg-slate-50/50">
            {section.fields?.map(renderField)}
          </div>
        )}
      </div>

      {/* Array Editor Dialog */}
      {editingArrayField && (
        <ArrayEditorDialog
          open={arrayEditorOpen}
          onOpenChange={setArrayEditorOpen}
          field={editingArrayField}
          value={data[editingArrayField.key]}
          onChange={(newValue) => handleFieldChange(editingArrayField.key, newValue)}
        />
      )}
    </>
  );
};

// Preview Component
const EmailPreview = ({ html, viewMode }) => {
  const width = viewMode === 'mobile' ? '375px' : '100%';
  
  return (
    <div 
      className="email-preview-frame mx-auto transition-all duration-300"
      style={{ width, maxWidth: '600px' }}
    >
      <iframe
        srcDoc={html}
        title="Email Preview"
        className="w-full h-full min-h-[600px] bg-white rounded-lg border border-slate-200"
        sandbox="allow-same-origin"
      />
    </div>
  );
};

export const CampaignBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { credits, refreshCredits } = useCredits();
  const { company } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Data states
  const [templates, setTemplates] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [messageTypes, setMessageTypes] = useState([]);
  const [segments, setSegments] = useState([]);
  
  // Campaign data
  const [campaign, setCampaign] = useState({
    channel: 'email',
    segmentId: '',
    messageType: '',
    sector: '',
    templateId: '',
    subject: '',
  });
  
  // Template editor state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sectionsData, setSectionsData] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [viewMode, setViewMode] = useState('desktop');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  // AI Modules state - Check if company has AI services enabled
  const aiModulesEnabled = company?.aiServices || { text: true, image: true, design: true }; // Default enabled for testing
  const [aiModules, setAiModules] = useState({
    text: false,
    image: false,
    design: false
  });
  const [aiProcessing, setAiProcessing] = useState({
    text: false,
    image: false,
    design: false
  });
  const [aiTextInput, setAiTextInput] = useState('');
  const [showAiTextDialog, setShowAiTextDialog] = useState(false);
  const [showAiImageDialog, setShowAiImageDialog] = useState(false);
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);

  // Scheduling state
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [sending, setSending] = useState(false);

  // AI Image - target section state
  const [aiImageTargetSection, setAiImageTargetSection] = useState(null);

  // n8n generation state
  const [n8nGenerating, setN8nGenerating] = useState({
    text: false,
    image: false,
    complete: false
  });

  // n8n Generate Text Function
  const handleN8nGenerateText = async () => {
    if (!selectedTemplate) {
      toast.error('No hay plantilla seleccionada');
      return;
    }

    // Filter out header and footer sections - they're managed by "Mi Empresa"
    const editableSections = selectedTemplate.sections.filter(
      s => s.type !== 'header' && s.type !== 'footer'
    );

    setN8nGenerating(prev => ({ ...prev, text: true }));
    try {
      const response = await n8nApi.generateText({
        templateName: selectedTemplate.name,
        sections: editableSections,
        existingData: sectionsData,
        messageType: campaign.messageType,
        sector: campaign.sector,
        subject: campaign.subject
      });

      console.log('n8n response:', response.data);

      if (response.data.success && response.data.sections) {
        // Merge generated content with existing data
        setSectionsData(prev => {
          const newData = { ...prev };
          Object.entries(response.data.sections).forEach(([sectionId, fields]) => {
            console.log(`Updating section ${sectionId}:`, fields);
            newData[sectionId] = { ...newData[sectionId], ...fields };
          });
          console.log('New sectionsData:', newData);
          return newData;
        });
        
        // Update subject if provided by n8n
        if (response.data.subject) {
          setCampaign(prev => ({ ...prev, subject: response.data.subject }));
          console.log('Updated subject:', response.data.subject);
        }
        
        toast.success('¡Textos generados con IA!');
        // Force preview update
        setTimeout(() => generatePreview(), 100);
      } else {
        const errorMsg = response.data.error || 'No se pudo generar el contenido';
        console.error('n8n error:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('n8n text generation error:', error);
      toast.error('Error al generar textos con n8n');
    } finally {
      setN8nGenerating(prev => ({ ...prev, text: false }));
    }
  };

  // n8n Generate Image Function (for current target section)
  const handleN8nGenerateImage = async () => {
    if (!aiImageTargetSection || !aiImagePrompt.trim()) {
      toast.error('Selecciona una sección y describe la imagen');
      return;
    }

    setN8nGenerating(prev => ({ ...prev, image: true }));
    try {
      const response = await n8nApi.generateImage({
        sectionId: aiImageTargetSection.id,
        sectionType: aiImageTargetSection.type,
        sectionLabel: aiImageTargetSection.label,
        fieldKey: aiImageTargetSection.imageField?.key || 'imageUrl',
        size: aiImageTargetSection.recommendedSize || '1024x1024',
        context: aiImagePrompt,
        subject: campaign.subject,
        messageType: campaign.messageType,
        sector: campaign.sector
      });

      if (response.data.success && response.data.imageUrl) {
        setGeneratedImage(response.data.imageUrl);
        toast.success('¡Imagen generada con IA!');
      } else {
        toast.error('No se pudo generar la imagen');
      }
    } catch (error) {
      console.error('n8n image generation error:', error);
      toast.error('Error al generar imagen con n8n');
    } finally {
      setN8nGenerating(prev => ({ ...prev, image: false }));
    }
  };

  // n8n Generate Complete Function
  const handleN8nGenerateComplete = async () => {
    if (!selectedTemplate) {
      toast.error('No hay plantilla seleccionada');
      return;
    }

    // Filter out header and footer sections - they're managed by "Mi Empresa"
    const editableSections = selectedTemplate.sections.filter(
      s => s.type !== 'header' && s.type !== 'footer'
    );

    setN8nGenerating(prev => ({ ...prev, complete: true }));
    try {
      const response = await n8nApi.generateComplete({
        templateName: selectedTemplate.name,
        sections: editableSections,
        existingData: sectionsData,
        messageType: campaign.messageType,
        sector: campaign.sector,
        subject: campaign.subject
      });

      console.log('n8n complete response:', response.data);

      if (response.data.success && response.data.sections) {
        // Merge generated content with existing data
        setSectionsData(prev => {
          const newData = { ...prev };
          Object.entries(response.data.sections).forEach(([sectionId, fields]) => {
            newData[sectionId] = { ...newData[sectionId], ...fields };
          });
          return newData;
        });
        
        // Update subject if provided by n8n
        if (response.data.subject) {
          setCampaign(prev => ({ ...prev, subject: response.data.subject }));
        }
        
        // Update section styles if provided by n8n (new design)
        if (response.data.styles && Object.keys(response.data.styles).length > 0) {
          setSelectedTemplate(prev => {
            if (!prev) return prev;
            const updatedSections = prev.sections.map(section => {
              const newStyles = response.data.styles[section.label] || response.data.styles[section.id];
              if (newStyles) {
                return {
                  ...section,
                  styles: { ...section.styles, ...newStyles }
                };
              }
              return section;
            });
            return { ...prev, sections: updatedSections };
          });
          console.log('Applied new styles from n8n:', response.data.styles);
        }
        
        toast.success('¡Contenido completo generado con IA!');
        // Force preview update
        setTimeout(() => generatePreview(), 100);
      } else {
        const errorMsg = response.data.error || 'No se pudo generar el contenido';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('n8n complete generation error:', error);
      toast.error('Error al generar contenido con n8n');
    } finally {
      setN8nGenerating(prev => ({ ...prev, complete: false }));
    }
  };

  // Get image size based on section type
  const getImageSizeForSection = (sectionType) => {
    const horizontalSections = ['hero', 'banner', 'header', 'twoColumn'];
    const squareSections = ['productList', 'image'];
    
    if (horizontalSections.includes(sectionType)) {
      return '1536x1024'; // Horizontal/Landscape
    } else if (squareSections.includes(sectionType)) {
      return '1024x1024'; // Square
    }
    return '1024x1024'; // Default square
  };

  // Get sections that have image fields (including productList with images in arrays)
  // Excludes header and footer sections as they're managed by "Mi Empresa"
  const getSectionsWithImages = () => {
    if (!selectedTemplate?.sections) return [];
    
    const sectionsWithImages = [];
    
    selectedTemplate.sections
      .filter(s => s.type !== 'header' && s.type !== 'footer')
      .forEach(s => {
      // Check for direct image fields
      const imageField = s.fields?.find(f => f.type === 'image');
      if (imageField) {
        sectionsWithImages.push({
          ...s,
          imageField,
          imageType: 'direct',
          recommendedSize: getImageSizeForSection(s.type)
        });
      }
      
      // Check for productList sections (products have imageUrl)
      if (s.type === 'productList') {
        const productsField = s.fields?.find(f => f.key === 'products' && f.type === 'array');
        if (productsField) {
          sectionsWithImages.push({
            ...s,
            imageField: { key: 'products', type: 'productImages', label: 'Imágenes de productos' },
            imageType: 'products',
            recommendedSize: '1024x1024' // Products are always square
          });
        }
      }
    });
    
    return sectionsWithImages;
  };

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Build params to filter templates by company sector
        const templateParams = {};
        if (company?.sector) {
          templateParams.filterBySector = true;
          templateParams.sector = company.sector;
        }
        
        const [templatesRes, sectorsRes, typesRes, segmentsRes] = await Promise.all([
          templatesApi.list(templateParams),
          sectorsApi.list(),
          messageTypesApi.list(),
          mauticApi.getSegments(),
        ]);
        setTemplates(templatesRes.data.templates);
        setSectors(sectorsRes.data.sectors);
        setMessageTypes(typesRes.data.messageTypes);
        setSegments(segmentsRes.data.segments);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al cargar datos');
      }
    };
    fetchData();
  }, [company?.sector]);

  // Handle template from URL or generated
  useEffect(() => {
    const templateId = searchParams.get('templateId');
    const generatedTemplate = location.state?.generatedTemplate;
    
    if (generatedTemplate) {
      setSelectedTemplate(generatedTemplate);
      initializeSectionsData(generatedTemplate);
      setStep(4);
    } else if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setSelectedTemplate(template);
        setCampaign(prev => ({ ...prev, templateId }));
        initializeSectionsData(template);
        setStep(4);
      }
    }
  }, [searchParams, templates, location.state]);

  const initializeSectionsData = (template) => {
    const data = {};
    const expanded = {};
    template.sections?.forEach((section, index) => {
      data[section.id] = {};
      section.fields?.forEach(field => {
        data[section.id][field.key] = field.default;
      });
      expanded[section.id] = index === 0; // Expand first section by default
    });
    setSectionsData(data);
    setExpandedSections(expanded);
  };

  const handleSectionDataChange = (sectionId, data) => {
    setSectionsData(prev => ({
      ...prev,
      [sectionId]: data
    }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const generatePreview = useCallback(async () => {
    if (!selectedTemplate) return;
    
    setPreviewLoading(true);
    try {
      const response = await templatesApi.preview({
        templateId: selectedTemplate.id,
        sectionsData,
        subject: campaign.subject,
        // Send custom sections if they've been modified by AI
        sections: selectedTemplate.sections,
      });
      setPreviewHtml(response.data.html);
    } catch (error) {
      console.error('Preview error:', error);
      // Use a fallback preview
      setPreviewHtml('<html><body><p style="padding:20px;text-align:center;color:#666;">Vista previa no disponible</p></body></html>');
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedTemplate, sectionsData, campaign.subject]);

  // Auto-generate preview when data changes
  useEffect(() => {
    if (step === 4 && selectedTemplate) {
      const debounce = setTimeout(() => {
        generatePreview();
      }, 500);
      return () => clearTimeout(debounce);
    }
  }, [step, selectedTemplate, sectionsData, generatePreview]);

  const handleImproveDesign = async (scope = 'full') => {
    if (credits.balance < (scope === 'full' ? 2 : 1)) {
      toast.error('Créditos insuficientes');
      return;
    }

    setLoading(true);
    try {
      const response = await aiApi.improveDesign({
        sections: selectedTemplate.sections,
        messageType: campaign.messageType,
        sector: campaign.sector,
        scope,
      });
      
      const newSections = response.data.sections;
      
      // Map old section data to new sections by type (not ID, since IDs may change)
      const newSectionsData = {};
      newSections.forEach(newSection => {
        // Try to find matching old section by type
        const oldSection = selectedTemplate.sections.find(s => s.type === newSection.type);
        if (oldSection && sectionsData[oldSection.id]) {
          newSectionsData[newSection.id] = sectionsData[oldSection.id];
        }
      });
      
      // Update template with improved design
      setSelectedTemplate(prev => ({
        ...prev,
        sections: newSections,
      }));
      
      // Update sections data to match new section IDs
      setSectionsData(newSectionsData);
      
      // Initialize expanded state for new sections
      const newExpanded = {};
      newSections.forEach(s => {
        newExpanded[s.id] = true;
      });
      setExpandedSections(newExpanded);
      
      refreshCredits();
      toast.success('¡Diseño mejorado! Revisa los cambios en la vista previa.');
    } catch (error) {
      console.error('Design improvement error:', error);
      toast.error('Error al mejorar el diseño');
    } finally {
      setLoading(false);
    }
  };

  // AI Module: Generate/Improve Text
  const handleAiTextGenerate = async () => {
    if (!aiTextInput.trim()) {
      toast.error('Por favor, introduce un texto para mejorar');
      return;
    }
    if (credits.balance < 1) {
      toast.error('Créditos insuficientes');
      return;
    }

    setAiProcessing(prev => ({ ...prev, text: true }));
    try {
      const response = await aiApi.improveField({
        text: aiTextInput,
        context: campaign.subject,
        messageType: campaign.messageType,
        fieldType: 'content',
        sector: campaign.sector,
      });
      
      // Show the improved text
      setAiTextInput(response.data.improved);
      refreshCredits();
      toast.success('Texto mejorado con IA');
    } catch (error) {
      toast.error('Error al mejorar el texto');
    } finally {
      setAiProcessing(prev => ({ ...prev, text: false }));
    }
  };

  const applyAiTextToSection = (sectionId, fieldKey) => {
    if (aiTextInput.trim()) {
      setSectionsData(prev => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          [fieldKey]: aiTextInput
        }
      }));
      setShowAiTextDialog(false);
      setAiTextInput('');
      toast.success('Texto aplicado a la sección');
    }
  };

  // AI Module: Generate Image
  const handleAiImageGenerate = async () => {
    if (!aiImagePrompt.trim()) {
      toast.error('Por favor, describe la imagen que deseas generar');
      return;
    }
    if (!aiImageTargetSection) {
      toast.error('Selecciona primero la sección destino');
      return;
    }
    if (credits.balance < 3) {
      toast.error('Créditos insuficientes (necesitas 3 créditos)');
      return;
    }

    const imageSize = getImageSizeForSection(aiImageTargetSection.type);
    
    setAiProcessing(prev => ({ ...prev, image: true }));
    try {
      const response = await aiApi.generateImage({
        prompt: aiImagePrompt,
        style: 'professional',
        size: imageSize,
        sector: campaign.sector,
      });
      
      // Store the generated image
      const imageUrl = `data:image/png;base64,${response.data.image}`;
      setGeneratedImage(imageUrl);
      refreshCredits();
      toast.success('Imagen generada correctamente');
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error('Error al generar la imagen. Intenta con otra descripción.');
    } finally {
      setAiProcessing(prev => ({ ...prev, image: false }));
    }
  };

  const applyAiImageToSection = (productIndex = null) => {
    if (generatedImage && aiImageTargetSection) {
      // Handle product images
      if (aiImageTargetSection.imageType === 'products' && productIndex !== null) {
        const currentProducts = sectionsData[aiImageTargetSection.id]?.products || 
          aiImageTargetSection.fields?.find(f => f.key === 'products')?.default || [];
        
        const updatedProducts = [...currentProducts];
        if (updatedProducts[productIndex]) {
          updatedProducts[productIndex] = {
            ...updatedProducts[productIndex],
            imageUrl: generatedImage
          };
        }
        
        setSectionsData(prev => ({
          ...prev,
          [aiImageTargetSection.id]: {
            ...prev[aiImageTargetSection.id],
            products: updatedProducts
          }
        }));
        toast.success(`Imagen aplicada al producto ${productIndex + 1}`);
        return; // Don't close dialog, allow applying to more products
      }
      
      // Handle direct image fields
      const imageField = aiImageTargetSection.fields?.find(f => f.type === 'image');
      if (imageField) {
        setSectionsData(prev => ({
          ...prev,
          [aiImageTargetSection.id]: {
            ...prev[aiImageTargetSection.id],
            [imageField.key]: generatedImage
          }
        }));
        setShowAiImageDialog(false);
        setGeneratedImage(null);
        setAiImagePrompt('');
        setAiImageTargetSection(null);
        toast.success(`Imagen aplicada a "${aiImageTargetSection.label}"`);
      }
    }
  };

  // AI Module: Generate Design (uses existing handleImproveDesign)
  const handleAiDesignGenerate = async () => {
    if (!aiModules.design) {
      toast.error('Activa el módulo de diseño primero');
      return;
    }
    setAiProcessing(prev => ({ ...prev, design: true }));
    try {
      await handleImproveDesign('full');
    } finally {
      setAiProcessing(prev => ({ ...prev, design: false }));
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await mauticApi.sendEmail({
        templateId: selectedTemplate.id,
        sectionsData,
        subject: campaign.subject,
        segmentId: campaign.segmentId,
        sections: selectedTemplate.sections, // Include custom sections
      });
      toast.success('¡Campaña enviada correctamente!');
      navigate('/campaigns');
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Error al enviar la campaña');
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime) {
      toast.error('Selecciona fecha y hora');
      return;
    }

    setSending(true);
    try {
      const scheduledDateTime = `${scheduleDate}T${scheduleTime}:00`;
      await mauticApi.sendEmail({
        templateId: selectedTemplate.id,
        sectionsData,
        subject: campaign.subject,
        segmentId: campaign.segmentId,
        sections: selectedTemplate.sections,
        scheduledAt: scheduledDateTime,
      });
      toast.success(`Campaña programada para ${scheduleDate} a las ${scheduleTime}`);
      setShowScheduleDialog(false);
      navigate('/campaigns');
    } catch (error) {
      console.error('Schedule error:', error);
      toast.error('Error al programar la campaña');
    } finally {
      setSending(false);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setCampaign(prev => ({ ...prev, templateId: template.id }));
    initializeSectionsData(template);
  };

  // Set sector from company on load
  useEffect(() => {
    if (company?.sector && !campaign.sector) {
      setCampaign(prev => ({ ...prev, sector: company.sector }));
    }
  }, [company?.sector]);

  // Step content renderers
  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold">Selecciona la audiencia</Label>
        <p className="text-sm text-slate-500 mt-1">Elige a quién enviar esta campaña</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {segments.map((segment) => (
          <button
            key={segment.id}
            onClick={() => setCampaign(prev => ({ ...prev, segmentId: segment.id?.toString() || segment.alias }))}
            className={`p-5 rounded-xl border-2 text-left transition-all ${
              campaign.segmentId === segment.id?.toString() || campaign.segmentId === segment.alias
                ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
            }`}
            data-testid={`segment-card-${segment.id || segment.alias}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  <p className="font-semibold text-slate-900">{segment.name}</p>
                </div>
                {segment.description && (
                  <p className="text-sm text-slate-500 mt-2">{segment.description}</p>
                )}
              </div>
              <Badge 
                variant={campaign.segmentId === segment.id?.toString() || campaign.segmentId === segment.alias ? 'default' : 'outline'}
                className="ml-2"
              >
                {segment.count || 0} contactos
              </Badge>
            </div>
            {(campaign.segmentId === segment.id?.toString() || campaign.segmentId === segment.alias) && (
              <div className="mt-3 flex items-center text-indigo-600 text-sm font-medium">
                <CheckCircle className="w-4 h-4 mr-1" />
                Seleccionado
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <Label>Tipo de mensaje</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
          {messageTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setCampaign(prev => ({ ...prev, messageType: type.id }))}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                campaign.messageType === type.id
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`message-type-${type.id}`}
            >
              <p className="font-medium text-slate-900">{type.name}</p>
              <p className="text-xs text-slate-500 mt-1">{type.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const filteredTemplates = campaign.messageType 
      ? templates.filter(t => t.messageTypes?.includes(campaign.messageType))
      : templates;
    
    // Find sector by ID or name (company.sector might be stored as name or id)
    const currentSector = sectors.find(s => 
      s.id === campaign.sector || s.name === campaign.sector
    );

    return (
      <div className="space-y-6">
        {/* Sector indicator */}
        {currentSector && (
          <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
            <FileText className="w-4 h-4 text-indigo-600" />
            <span className="text-sm text-slate-600">Sector:</span>
            <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
              {currentSector.name}
            </Badge>
            <span className="text-xs text-slate-500 ml-auto">
              Mostrando plantillas para tu sector
            </span>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <Label>Selecciona una plantilla</Label>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/templates/generate')}
            className="text-indigo-600"
            data-testid="generate-new-template"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Generar nueva
          </Button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <button
              key={template.id}
              onClick={() => selectTemplate(template)}
              className={`rounded-lg border-2 overflow-hidden text-left transition-all ${
                selectedTemplate?.id === template.id
                  ? 'border-indigo-500 ring-2 ring-indigo-200'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`select-template-${template.id}`}
            >
              <div className="aspect-video bg-slate-100">
                {template.thumbnail && (
                  <img src={template.thumbnail} alt={template.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-3">
                <p className="font-medium text-slate-900 text-sm">{template.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{template.sections?.length || 0} secciones</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
      {/* Left Panel - Sections Editor */}
      <div className="col-span-5 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Editar secciones</h3>
        </div>

        {/* AI Modules Panel */}
        <Card className="mb-4 border-indigo-100 bg-gradient-to-r from-indigo-50/50 to-purple-50/50">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <CardTitle className="text-sm font-medium">Generación con IA (n8n)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0 space-y-3">
            {/* Main n8n generation buttons */}
            <div className="grid grid-cols-3 gap-2">
              {/* Generate Text with n8n */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleN8nGenerateText}
                disabled={n8nGenerating.text || !campaign.subject}
                className="flex flex-col h-auto py-2 hover:bg-indigo-50 hover:border-indigo-300"
                data-testid="n8n-generate-text"
              >
                {n8nGenerating.text ? (
                  <Loader2 className="w-5 h-5 mb-1 animate-spin text-indigo-600" />
                ) : (
                  <Type className="w-5 h-5 mb-1 text-indigo-600" />
                )}
                <span className="text-xs">Textos</span>
              </Button>
              
              {/* Generate Images with n8n */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAiImageDialog(true)}
                disabled={n8nGenerating.image}
                className="flex flex-col h-auto py-2 hover:bg-purple-50 hover:border-purple-300"
                data-testid="n8n-generate-image"
              >
                {n8nGenerating.image ? (
                  <Loader2 className="w-5 h-5 mb-1 animate-spin text-purple-600" />
                ) : (
                  <ImageIcon className="w-5 h-5 mb-1 text-purple-600" />
                )}
                <span className="text-xs">Fotos</span>
              </Button>
              
              {/* Generate New Design with n8n */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleN8nGenerateComplete}
                disabled={n8nGenerating.complete || !campaign.subject}
                className="flex flex-col h-auto py-2 hover:bg-emerald-50 hover:border-emerald-300"
                data-testid="n8n-generate-complete"
              >
                {n8nGenerating.complete ? (
                  <Loader2 className="w-5 h-5 mb-1 animate-spin text-emerald-600" />
                ) : (
                  <Palette className="w-5 h-5 mb-1 text-emerald-600" />
                )}
                <span className="text-xs">Diseño</span>
              </Button>
            </div>
            
            {!campaign.subject && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                Escribe el asunto del email para activar la generación
              </p>
            )}
          </CardContent>
        </Card>
        
        <div className="mb-4">
          <Label htmlFor="subject">Asunto del email</Label>
          <Input
            id="subject"
            value={campaign.subject}
            onChange={(e) => setCampaign(prev => ({ ...prev, subject: e.target.value }))}
            placeholder="Escribe el asunto..."
            className="mt-2"
            data-testid="email-subject"
          />
        </div>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-3">
            {selectedTemplate?.sections
              ?.filter(section => section.type !== 'header' && section.type !== 'footer')
              .map((section) => (
              <SectionEditor
                key={section.id}
                section={section}
                data={sectionsData[section.id] || {}}
                onChange={(data) => handleSectionDataChange(section.id, data)}
                messageType={campaign.messageType}
                sector={campaign.sector}
                expanded={expandedSections[section.id]}
                onToggle={() => toggleSection(section.id)}
              />
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Right Panel - Preview */}
      <div className="col-span-7 flex flex-col bg-slate-100 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Vista previa</h3>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'desktop' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('desktop')}
              data-testid="preview-desktop"
            >
              <Monitor className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'mobile' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('mobile')}
              data-testid="preview-mobile"
            >
              <Smartphone className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreviewDialog(true)}
              data-testid="fullscreen-preview"
            >
              <Eye className="w-4 h-4 mr-1" />
              Ampliar
            </Button>
          </div>
        </div>
        
        <div className="flex-1 overflow-auto">
          {previewLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <EmailPreview html={previewHtml} viewMode={viewMode} />
          )}
        </div>
      </div>

      {/* AI Text Dialog */}
      <Dialog open={showAiTextDialog} onOpenChange={setShowAiTextDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Type className="w-5 h-5 text-indigo-600" />
              Generar Texto con IA
            </DialogTitle>
            <DialogDescription>
              Escribe tu texto y la IA lo mejorará para email marketing
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tu texto</Label>
              <Textarea
                value={aiTextInput}
                onChange={(e) => setAiTextInput(e.target.value)}
                placeholder="Escribe el texto que deseas mejorar..."
                className="mt-2 min-h-[150px]"
                data-testid="ai-text-input"
              />
            </div>
            <Button 
              onClick={handleAiTextGenerate}
              disabled={aiProcessing.text || !aiTextInput.trim()}
              className="w-full"
              data-testid="generate-text-btn"
            >
              {aiProcessing.text ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Mejorar texto (1 cr)
            </Button>
            
            {aiTextInput && (
              <div className="pt-4 border-t">
                <Label className="text-xs text-slate-500">Aplicar a sección:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedTemplate?.sections?.filter(s => 
                    s.fields?.some(f => f.type === 'textarea' || (f.type === 'text' && f.aiImprove))
                  ).map(section => (
                    <Button
                      key={section.id}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const textField = section.fields?.find(f => f.type === 'textarea' || f.key === 'content');
                        if (textField) {
                          applyAiTextToSection(section.id, textField.key);
                        }
                      }}
                    >
                      {section.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Image Dialog */}
      <Dialog open={showAiImageDialog} onOpenChange={(open) => {
        setShowAiImageDialog(open);
        if (!open) {
          setAiImageTargetSection(null);
          setGeneratedImage(null);
          setAiImagePrompt('');
        }
      }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-600" />
              Generar Imagen con IA
            </DialogTitle>
            <DialogDescription>
              Selecciona la sección y describe la imagen que necesitas
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4">
            {/* Step 1: Select target section */}
            <div>
              <Label className="text-sm font-medium">1. Sección destino</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {getSectionsWithImages().map(section => (
                  <button
                    key={section.id}
                    onClick={() => setAiImageTargetSection(section)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      aiImageTargetSection?.id === section.id
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                    }`}
                    data-testid={`select-section-${section.id}`}
                  >
                    <p className="font-medium text-sm text-slate-900">{section.label}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {section.recommendedSize === '1536x1024' ? '📐 Horizontal' : '⬜ Cuadrada'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Describe image (only if section selected) */}
            {aiImageTargetSection && (
              <>
                <div>
                  <Label className="text-sm font-medium">2. Descripción de la imagen</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Tamaño: {aiImageTargetSection.recommendedSize === '1536x1024' ? 'Horizontal (1536x1024)' : 'Cuadrada (1024x1024)'}
                  </p>
                  <Textarea
                    value={aiImagePrompt}
                    onChange={(e) => setAiImagePrompt(e.target.value)}
                    placeholder={`Ej: ${aiImageTargetSection.type === 'hero' 
                      ? 'Banner promocional con colores vibrantes y texto de oferta...' 
                      : 'Imagen profesional del producto sobre fondo blanco...'}`}
                    className="min-h-[100px]"
                    data-testid="ai-image-prompt"
                  />
                </div>
                
                <Button 
                  onClick={handleAiImageGenerate}
                  disabled={aiProcessing.image || !aiImagePrompt.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="generate-image-btn"
                >
                  {aiProcessing.image ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generando imagen {aiImageTargetSection.recommendedSize === '1536x1024' ? 'horizontal' : 'cuadrada'}...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Generar imagen (3 cr)
                    </>
                  )}
                </Button>
              </>
            )}
            
            {/* Generated image preview */}
            {generatedImage && (
              <div className="pt-4 border-t space-y-3">
                <Label className="text-xs text-slate-500">Imagen generada para "{aiImageTargetSection?.label}":</Label>
                <img 
                  src={generatedImage} 
                  alt="Generated" 
                  className="w-full max-h-48 object-contain rounded-lg border bg-slate-50"
                />
                
                {/* For product images - show product selection */}
                {aiImageTargetSection?.imageType === 'products' ? (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Aplicar al producto:</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(sectionsData[aiImageTargetSection.id]?.products || 
                        aiImageTargetSection.fields?.find(f => f.key === 'products')?.default || []
                      ).map((product, idx) => (
                        <Button 
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => applyAiImageToSection(idx)}
                          className="flex flex-col h-auto py-2"
                          data-testid={`apply-to-product-${idx}`}
                        >
                          <span className="text-xs font-medium">{product.name || `Producto ${idx + 1}`}</span>
                          <span className="text-xs text-slate-500">{product.price}</span>
                        </Button>
                      ))}
                    </div>
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowAiImageDialog(false);
                        setGeneratedImage(null);
                        setAiImagePrompt('');
                        setAiImageTargetSection(null);
                      }}
                      className="w-full mt-2"
                    >
                      Cerrar
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => applyAiImageToSection()}
                    className="w-full"
                    data-testid="apply-image-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aplicar a "{aiImageTargetSection?.label}"
                  </Button>
                )}
              </div>
            )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Get audience info for summary
  const getAudienceInfo = () => {
    const segment = segments.find(s => s.id?.toString() === campaign.segmentId || s.alias === campaign.segmentId);
    return segment || { name: 'Todos los contactos', count: 0 };
  };

  const renderStep5 = () => {
    const audience = getAudienceInfo();
    const messageType = messageTypes.find(t => t.id === campaign.messageType);
    const sector = sectors.find(s => s.id === campaign.sector);

    return (
      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
        {/* Left Panel - Summary */}
        <div className="col-span-5 flex flex-col">
          <h3 className="font-semibold text-slate-900 mb-4">Resumen de la Campaña</h3>
          
          <Card className="flex-1">
            <CardContent className="p-6 space-y-6">
              {/* Audience */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500">Audiencia</p>
                  <p className="font-medium text-slate-900">{audience.name}</p>
                  <p className="text-sm text-slate-500">{audience.count || '—'} contactos</p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>

              <Separator />

              {/* Message Type & Sector */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500">Tipo de mensaje</p>
                  <p className="font-medium text-slate-900">{messageType?.name || campaign.messageType}</p>
                  <p className="text-sm text-slate-500">Sector: {sector?.name || campaign.sector}</p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>

              <Separator />

              {/* Template */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500">Plantilla</p>
                  <p className="font-medium text-slate-900">{selectedTemplate?.name}</p>
                  <p className="text-sm text-slate-500">{selectedTemplate?.sections?.length || 0} secciones</p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>

              <Separator />

              {/* Subject */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500">Asunto del email</p>
                  <p className="font-medium text-slate-900">{campaign.subject || '(Sin asunto)'}</p>
                </div>
                {campaign.subject ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">Requerido</span>
                )}
              </div>

              <Separator />

              {/* Action Buttons */}
              <div className="space-y-3 pt-4">
                <Button
                  onClick={handleSend}
                  disabled={sending || !campaign.subject}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12"
                  data-testid="send-now-btn"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Enviar ahora
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowScheduleDialog(true)}
                  disabled={sending || !campaign.subject}
                  className="w-full h-12"
                  data-testid="schedule-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Programar envío
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Preview */}
        <div className="col-span-7 flex flex-col bg-slate-100 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Vista previa final</h3>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'desktop' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('desktop')}
              >
                <Monitor className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'mobile' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('mobile')}
              >
                <Smartphone className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <EmailPreview html={previewHtml} viewMode={viewMode} />
            )}
          </div>
        </div>

        {/* Schedule Dialog */}
        <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                Programar envío
              </DialogTitle>
              <DialogDescription>
                Selecciona la fecha y hora para enviar la campaña
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-2"
                  data-testid="schedule-date"
                />
              </div>
              <div>
                <Label>Hora</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="mt-2"
                  data-testid="schedule-time"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSchedule}
                disabled={!scheduleDate || !scheduleTime || sending}
                data-testid="confirm-schedule-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Clock className="w-4 h-4 mr-2" />
                )}
                Programar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const canProceed = () => {
    if (step === 1) return campaign.segmentId;
    if (step === 2) return campaign.messageType;
    if (step === 3) return selectedTemplate;
    if (step === 4) return campaign.subject;
    return true;
  };

  const steps = [
    { num: 1, title: 'Audiencia' },
    { num: 2, title: 'Tipo' },
    { num: 3, title: 'Plantilla' },
    { num: 4, title: 'Editar' },
    { num: 5, title: 'Enviar' },
  ];

  return (
    <div className="p-8 animate-fade-in" data-testid="campaign-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/campaigns')}
            className="mb-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step > 1 ? 'Anterior' : 'Cancelar'}
          </Button>
          <h1 className="text-2xl font-bold font-[Manrope] text-slate-900">Nueva Campaña</h1>
        </div>
        
        {step === 4 && (
          <Button
            onClick={() => setStep(5)}
            disabled={!canProceed()}
            className="ai-button text-white"
            data-testid="next-to-send-btn"
          >
            Siguiente
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <button
              onClick={() => s.num < step && setStep(s.num)}
              disabled={s.num > step}
              className="flex items-center"
            >
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step >= s.num 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {s.num}
              </div>
              <span className={`ml-2 text-sm font-medium ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>
                {s.title}
              </span>
            </button>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-4 ${step > s.num ? 'bg-indigo-600' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      {step < 4 ? (
        <Card className="max-w-3xl">
          <CardContent className="p-6">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}

            <div className="flex justify-end mt-8 pt-6 border-t">
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                data-testid="next-step-btn"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : step === 4 ? (
        renderStep4()
      ) : (
        renderStep5()
      )}

      {/* Fullscreen Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl h-[90vh]">
          <DialogHeader>
            <DialogTitle>Vista previa del email</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto p-4">
            <EmailPreview html={previewHtml} viewMode="desktop" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignBuilder;
